import { useState } from 'react'
import { Mail, Phone, MapPin, ArrowRight } from 'lucide-react'
import { motion } from 'framer-motion'
import Button from '../ui/Button'
import FormField from '../ui/FormField'
import useFormValidation from '../../hooks/useFormValidation'

const ContactSection = () => {
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  const { values, errors, handleChange, handleBlur, validateAll, reset } = useFormValidation(
    {
      name: '',
      email: '',
      phone: '',
      company: '',
      employees: '',
      objective: '',
      message: ''
    },
    {
      name: { required: true, minLength: 3, message: 'Nome deve ter no mínimo 3 caracteres' },
      email: { required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'E-mail inválido' },
      phone: { required: true, pattern: /^\(?[1-9]{2}\)? ?(?:[2-8]|9[1-9])[0-9]{3}\-?[0-9]{4}$/, message: 'Telefone inválido' },
      company: { required: true, minLength: 2, message: 'Nome da empresa deve ter no mínimo 2 caracteres' },
      employees: { required: true, message: 'Selecione o número de funcionários' },
      objective: { required: true, message: 'Selecione o objetivo' },
      message: { required: false }
    }
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateAll()) return
    if (!acceptedTerms) {
      alert('Você precisa aceitar a Política de Privacidade e os Termos de Uso')
      return
    }

    setSubmitStatus('loading')

    try {
      await new Promise(resolve => setTimeout(resolve, 2000))
      setSubmitStatus('success')
      reset()
      setAcceptedTerms(false)
      setTimeout(() => setSubmitStatus('idle'), 5000)
    } catch (error) {
      setSubmitStatus('error')
      setTimeout(() => setSubmitStatus('idle'), 5000)
    }
  }

  const employeeOptions = [
    { value: '1-10', label: '1 a 10 funcionários' },
    { value: '11-50', label: '11 a 50 funcionários' },
    { value: '51-100', label: '51 a 100 funcionários' },
    { value: '101-500', label: '101 a 500 funcionários' },
    { value: '500+', label: 'Mais de 500 funcionários' }
  ]

  const objectiveOptions = [
    { value: 'demo', label: 'Solicitar demonstração' },
    { value: 'info', label: 'Obter mais informações' },
    { value: 'quote', label: 'Solicitar orçamento' },
    { value: 'support', label: 'Suporte técnico' },
    { value: 'other', label: 'Outro' }
  ]

  const contactInfo = [
    { icon: Mail, title: 'E-mail', items: ['contato@flowclock.com.br', 'suporte@flowclock.com.br'] },
    { icon: Phone, title: 'Telefone', items: ['(11) 3000-0000', 'WhatsApp: (11) 99999-9999'] },
    { icon: MapPin, title: 'Endereço', items: ['Av. Paulista, 1000', 'São Paulo - SP, 01310-100'] }
  ]

  return (
    <section id="contato" className="py-20 bg-white dark:bg-gray-900">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm font-semibold mb-4">
            Entre em Contato
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Vamos transformar seu{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-primary-800">
              RH juntos
            </span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Preencha o formulário e nossa equipe entrará em contato em até 24 horas
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            {contactInfo.map((info, index) => {
              const Icon = info.icon
              return (
                <div
                  key={index}
                  className="flex gap-4 p-6 bg-gray-50 dark:bg-gray-800 rounded-xl hover:shadow-md transition-all duration-200"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white mb-2">{info.title}</h3>
                    {info.items.map((item, i) => (
                      <p key={i} className="text-gray-600 dark:text-gray-400 text-sm">{item}</p>
                    ))}
                  </div>
                </div>
              )
            })}

            <div className="p-6 bg-gradient-primary text-white rounded-xl">
              <h3 className="font-bold text-xl mb-3">Horário de Atendimento</h3>
              <p className="text-white/90 mb-1">Segunda a Sexta: 8h às 18h</p>
              <p className="text-white/90 mb-3">Sábado: 9h às 13h</p>
              <p className="text-sm font-semibold pt-3 border-t border-white/20">
                Suporte técnico 24/7 para clientes
              </p>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2"
          >
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8">
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
                  ✓ Mensagem enviada com sucesso! Entraremos em contato em breve.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
                  ✗ Erro ao enviar mensagem. Tente novamente.
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <FormField label="Nome Completo" name="name" placeholder="Seu nome" required value={values.name} onChange={handleChange} onBlur={handleBlur} error={errors.name} />
                  <FormField label="E-mail" name="email" type="email" placeholder="seu@email.com" required value={values.email} onChange={handleChange} onBlur={handleBlur} error={errors.email} />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <FormField label="Telefone" name="phone" type="tel" placeholder="(11) 98765-4321" required value={values.phone} onChange={handleChange} onBlur={handleBlur} error={errors.phone} />
                  <FormField label="Empresa" name="company" placeholder="Nome da sua empresa" required value={values.company} onChange={handleChange} onBlur={handleBlur} error={errors.company} />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <FormField label="Número de Funcionários" name="employees" as="select" required value={values.employees} onChange={handleChange} onBlur={handleBlur} error={errors.employees} options={employeeOptions} />
                  <FormField label="Objetivo" name="objective" as="select" required value={values.objective} onChange={handleChange} onBlur={handleBlur} error={errors.objective} options={objectiveOptions} />
                </div>

                <FormField label="Mensagem (Opcional)" name="message" as="textarea" placeholder="Conte-nos mais sobre suas necessidades..." value={values.message} onChange={handleChange} onBlur={handleBlur} error={errors.message} />

                <div className="flex items-start gap-3">
                  <input type="checkbox" id="terms" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="mt-1" />
                  <label htmlFor="terms" className="text-sm text-gray-600 dark:text-gray-400">
                    Aceito a <a href="#privacy" className="text-primary-600 hover:underline">Política de Privacidade</a> e os{' '}
                    <a href="#terms" className="text-primary-600 hover:underline">Termos de Uso</a>. Meus dados serão tratados conforme a LGPD.
                  </label>
                </div>

                <Button type="submit" size="lg" disabled={submitStatus === 'loading'} className="w-full group">
                  {submitStatus === 'loading' ? (
                    <>
                      <span className="spinner mr-2"></span>
                      Enviando...
                    </>
                  ) : (
                    <>
                      Enviar Mensagem
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default ContactSection
